import * as _ from 'lodash';
import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
import { CreditRequirements } from '../../../../../ng2/shared/constants/credit-requirements.constant';
import { IGapPlan } from '../../../../../ng2/shared/typings/interfaces/gap-plan.interface';
import { ISchool } from '../../../../../ng2/shared/typings/interfaces/school.interface';
import { WizardUtils } from '../../../../shared/services/wizard-utils/wizard-utils.service';
import { StudentStatic } from '../../../../shared/static-models/student-static.service';
import { SpinnerModal } from './../../../../shared/modals/status-spinner/status-spinner.service';

interface IGapPlanGrouping {
  groupOne: Object;
  groupTwo: Object;
}

interface INextTermPlan {
  planName: String;
  count: Number;
  studentIds: string[];
}

export class NextTermPlanningCtrl {
  static $inject = [
    'SDCStore',
    '$scope',
    'SpinnerModal',
    'WizardUtils',
    'CsvExporter',
    'ImSchool',
    'Rollbar',
    'UtilitiesService',
  ];
  public uiReady = false;
  public currentTermYear: number;
  public formattedDeptPlans: IGapPlanGrouping;
  public pageTitle;
  public gapPlans: IGapPlan[];
  public currentSchool: ISchool;
  public currentUser;
  public humanReadableTerm;
  public filteredTermYear;
  public terms: {
    [termYear: number]: string;
  };
  public plansPresentInTerm: boolean;

  private defaultTermYear;
  private termsFromGapPlans;

  constructor(
    private SDCStore: any,
    private $scope,
    private SpinnerModal: SpinnerModal,
    private WizardUtils: WizardUtils,
    private CsvExporter: any,
    private ImSchool: ImSchool,
    private Rollbar,
    private UtilitiesService: any,
  ) {}

  $onInit(): void {
    this.currentTermYear = this.currentSchool.currentTermYear;
    this.pageTitle = this.currentSchool.fullName + ' - Next Term Planning';
    this.$scope.sortType = 'count';
    this.$scope.sortReverse = true;

    this.$scope.$listenTo(this.SDCStore, ['ui', 'isLoading'], () => {
      const isLoading = this.SDCStore.getUiState().isLoading;
      if (!isLoading) {
        this.setUi();
        this.uiReady = true;
      }
    });
  }

  public setUi(): void {
    this.termsFromGapPlans = this.getTermYearsFromGapPlans();
    this.defaultTermYear = this.getDefaultTermYear();
    this.terms = this.getTermFilter();
    this.filteredTermYear = this.defaultTermYear;

    this.humanReadableTerm = this.UtilitiesService.getHumanReadableTerm(this.defaultTermYear);
    this.formattedDeptPlans = this.getFormattedDeptPlansForTermYear(this.defaultTermYear);
    this.SpinnerModal.deactivateSpinner();
  }

  getTermFilter(): { [termYear: number]: string } {
    let terms = {};
    const termsPresentInPlans = _.isEmpty(this.termsFromGapPlans);
    if (termsPresentInPlans) {
      // if there are no plans, set the filter to current year
      const readableTermYear = this.UtilitiesService.getHumanReadableTerm(this.currentTermYear);
      terms[this.currentTermYear] = readableTermYear;
    } else {
      terms = this.termsFromGapPlans;
    }
    return terms;
  }

  getDefaultTermYear(): number {
    // TODO: this is hack to get support end dates working for  'ANNUALIZED' schools
    // Further discussions required with data team to address issues pertaining to 'ANNUALIZED' schools `currentTermNumber` (CM)
    let termYear =
      this.currentSchool.termStructure === 'ANNUALIZED' && this.currentTermYear === 182 ? 187 : this.currentTermYear;
    const termsPresentInPlans = _.isEmpty(this.termsFromGapPlans);

    if (!termsPresentInPlans) {
      const nextTermYear = this.ImSchool.getNextTermYear(this.currentSchool);
      if (this.termsFromGapPlans[nextTermYear]) {
        termYear = nextTermYear;
      }
    }
    return termYear;
  }

  getTermYearsFromGapPlans(): { [key: number]: { prop: string } } {
    const terms = _.reduce(
      this.gapPlans,
      (result, plan: IGapPlan) => {
        if (!result[plan.termYear]) {
          const readableTermYear = this.UtilitiesService.getHumanReadableTerm(plan.termYear);
          result[plan.termYear] = readableTermYear;
        }
        return result;
      },
      {},
    );
    _.sortBy(terms, 'termYear');
    return terms;
  }

  onTermFilterChange(): void {
    const term = parseInt(this.filteredTermYear, 10);
    this.formattedDeptPlans = this.getFormattedDeptPlansForTermYear(term);
    this.humanReadableTerm = this.UtilitiesService.getHumanReadableTerm(term);
  }

  getFormattedDeptPlansForTermYear(nextTermYear: number): IGapPlanGrouping {
    const groupedDeptPlans = this.getGroupedGapPlansForTerm(nextTermYear);
    const sortedDeptMapKeys = this.getSortedMapKeys(groupedDeptPlans);
    const formattedDeptPlans = this.splitMaps(groupedDeptPlans, sortedDeptMapKeys);
    this.plansPresentInTerm = !_.isEmpty(groupedDeptPlans);
    return formattedDeptPlans;
  }

  // returns a map of gap plans and their count for a term year, grouped by departments
  getGroupedGapPlansForTerm(termYear: number): { [key: string]: { prop: INextTermPlan } } {
    // This is an alternate approach to creating Map.
    // Remove prototype completely to eliminate the risk of name conflicts in map.
    // To learn more, see: http://ryanmorr.com/true-hash-maps-in-javascript/ (Devin)
    let deptMap = Object.create(null); // map of dept and plans

    _.map(this.gapPlans, gapPlan => {
      if (gapPlan.status === 'ACTIVE' && gapPlan.termYear === termYear) {
        let plan = gapPlan.plan && gapPlan.plan.toUpperCase();
        const subject = gapPlan.gradReq;
        const dept = (subject && this.getDepartmentForPlan(subject)) || 'Total';
        const districtId = StudentStatic.getDistrictIdFromStudentId(gapPlan.student.studentId);

        // there's a possibility of not having a plan. In that case,
        // use 'note' instead.
        if (!plan) {
          plan = gapPlan.note && gapPlan.note.toUpperCase();
        }

        if (dept in deptMap) {
          let deptPlanMap = deptMap[dept]; // array of all plans in this dept
          const planIndex = deptPlanMap.findIndex(obj => obj.planName === plan);

          if (planIndex >= 0) {
            // Plan exists in map. Add the student only if not in plan already
            let planStudents = deptPlanMap[planIndex].studentIds;
            if (planStudents.indexOf(districtId) === -1) {
              deptPlanMap[planIndex].count++;
              deptPlanMap[planIndex].studentIds.push(districtId);
            }
          } else {
            let newPlanMap: INextTermPlan = Object.create(null);
            newPlanMap = { planName: plan, count: 1, studentIds: [districtId] };
            deptMap[dept].push(newPlanMap);
          }
        } else {
          // Dept not in map
          let newPlanMap = Object.create(null);
          newPlanMap = { planName: plan, count: 1, studentIds: [districtId] };
          deptMap[dept] = [newPlanMap];
        }
      }
    });

    return deptMap;
  }

  getSortedMapKeys(map): string[] {
    // sort the map in descending, based on the number of plans
    const sortedMapKeys = Object.keys(map).sort(function(a, b) {
      return map[b].length - map[a].length;
    });

    return sortedMapKeys;
  }

  // split the department groups into two groups. (for UI purposes)
  splitMaps(map, keys): IGapPlanGrouping {
    const sortedDeptGroupOne = Object.create(null);
    const sortedDeptGroupTwo = Object.create(null);

    keys.forEach((dept, index) => {
      if (++index % 2) {
        // above logic ensures that a department group always starts with group one,
        // so that on the front end you don't get an empty group one when there is only one department to show (Devin)
        sortedDeptGroupOne[dept] = map[dept];
      } else {
        sortedDeptGroupTwo[dept] = map[dept];
      }
    });
    const splitMaps: IGapPlanGrouping = { groupOne: sortedDeptGroupOne, groupTwo: sortedDeptGroupTwo };
    return splitMaps;
  }

  getDepartmentForPlan(gradReq: string): string {
    const dept: any = _.find(CreditRequirements, { camelCase: gradReq });

    if (dept.department) {
      return dept.department.human;
    }
  }

  generateCsv(): void {
    const docType = 'text/csv;charset=utf-8';
    const termYear = parseInt(this.filteredTermYear, 10);
    const currentDateAndTime = moment().format('MM-DD-YYYY hmma');
    const fileName = `Planned Courses for ${this.humanReadableTerm} ${currentDateAndTime}.csv`;

    this.SpinnerModal.activateSpinner('Generating Export');
    this.CsvExporter.createNextTermPlanningCsv(this.currentSchool, termYear)
      .then(csvString => {
        this.SpinnerModal.deactivateSpinner();
        this.CsvExporter.exportCsv(csvString, fileName, docType);
      })
      .catch(err => {
        this.Rollbar.error(`Unable to complete Next Term Planning CSV export`, err);
      });
  }
}
